/**
 * Painel "Revisão humana" — pedido da Mariana em 30/09/2026: *"na lista de revisão humana, deixar
 * os botões de entrar e sair — abrir todas as campanhas disponíveis para aquele anúncio com uma
 * setinha embaixo"*. Um anúncio por linha (o motor gera várias linhas por anúncio); a setinha
 * abre TODAS as promoções do anúncio (ativas e candidatas) com o botão de cada uma: SAIR nas
 * ativas, Participar nas candidatas (o mesmo modal da aba Por anúncio, com o seletor de preço).
 *
 * Aqui é ELA que decide — o robô não entra em disputa nem relâmpago nesses anúncios (cupom sim).
 * Os botões escrevem de verdade quando o serviço está no ar, com a confirmação em dois cliques.
 *
 * 19/10/2026, ela: "tem dois lugares com revisão humana, uma aba chamada revisão humana e outra na
 * tela do robô, prefiro manter só a aba de revisão humana". A seção "⚠️ Revisão humana — ativa com
 * margem entre X% e Y%" saiu da aba Robô; o que ela tinha de exclusivo veio para cá: toda linha
 * `alertar` entra na lista (mesmo sem marca `revisao_humana`), o SAIR em massa das selecionadas
 * (cupom fica fora, como lá) e o botão "exceção" (abre o painel de exceções na aba Robô).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Checkbox, Collapse, Stack, Typography } from '@mui/material'
import { classeMc, mcDe, pedirExcecao, selKey, type Anuncio, type DecisaoMotor, type Promocao } from './logic'
import { usePromo } from './ctx'
import { fetchFila, fetchLiberacoes, fetchVistos, registrarDecisao, removerLiberacao, removerVisto, salvarLiberacao, salvarVisto, type Liberacao, type Visto } from './promocoesService'
import { AcaoCell, IncMark, LARANJA, McTip, P, Panel, StatusPill, brl, mono, pct, useArmado, useCorMc } from './ui'

/** Revisão por motivo de CMV (margem acima de 25%, custo igual ou maior que o preço, CMV velho) é a que ELA
 *  fecha com um clique (01/10/2026): "eu confiro manual e libero quer dizer que está certo". As que pedem dado
 *  (sem CMV, sem frete/comissão/imposto, só-status) não têm o que liberar. */
const MOTIVO_MARGEM_ALTA = /acima de 25|confira o cadastro|CMV sem atualizar/

/** Chave do "visto": os motivos sem números (preço/percentual mudam todo dia; o que importa é o TIPO do motivo).
 *  Mudou o tipo do motivo, a linha volta a aparecer. */
const chaveVisto = (motivos: string[]) => motivos.map((m) => m.replace(/[\d.,]+/g, '#').slice(0, 60)).sort().join(' | ')

const ROTULO_ROBO: Record<string, string> = {
  sair_auto: 'robô SAI', alertar: 'fica ativa, decide você', bloqueado: 'robô não entra', trocar: 'robô troca', entrar: 'robô entra',
  relampago: 'relâmpago', cupom: 'cupom', nada: 'robô não mexe',
}

interface Grupo { a: Anuncio; decs: DecisaoMotor[]; motivos: string[]; sai: number }

/** Promoções ativas que o SAIR em massa tira neste anúncio: as das linhas `alertar`/`sair_auto` do motor,
 *  menos cupom (fora de qualquer ação em massa desde 05/10/2026). Mesma regra que a seção antiga da aba Robô. */
function saiveis(g: Grupo): Array<{ p: Promocao; d: DecisaoMotor }> {
  const out: Array<{ p: Promocao; d: DecisaoMotor }> = []
  for (const d of g.decs) {
    if ((d.acao !== 'alertar' && d.acao !== 'sair_auto') || d.promocao_tipo === 'SELLER_COUPON_CAMPAIGN') continue
    const p = g.a.promocoes.find((x) => x.type === d.promocao_tipo && (d.promocao_id ? x.id === d.promocao_id : true) && (x.status === 'started' || x.status === 'pending'))
    if (p && !out.some((o) => o.p === p)) out.push({ p, d })
  }
  return out
}

export function RevisaoHumana() {
  const ctx = usePromo()
  const { fila, contaId, conta } = ctx
  const [liberacoes, setLiberacoes] = useState<Liberacao[]>([])
  const [vistos, setVistos] = useState<Visto[]>([])
  const [mostrarVistos, setMostrarVistos] = useState(false)
  const carregarVistos = useCallback(() => {
    void fetchVistos().then(setVistos)
  }, [])
  useEffect(() => {
    carregarVistos()
  }, [carregarVistos])
  /** Ela, 01/10/2026: "se for para deixar [na revisão] preciso ter um botão de ok para tirar eles do painel".
   *  O visto só esconde a linha enquanto o motivo for o mesmo; não muda nada no robô. */
  const marcarVisto = async (a: Anuncio, motivos: string[], desfazer: boolean) => {
    setBusyLib(a.mlb)
    try {
      if (desfazer) await removerVisto(a.mlb)
      else await salvarVisto({ mlb: a.mlb, chave: chaveVisto(motivos), titulo: a.titulo, conta: contaId })
      carregarVistos()
    } catch (e) {
      setMsgLib(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyLib(null)
    }
  }
  const vistoPor = useMemo(() => new Map(vistos.map((v) => [v.mlb, v] as const)), [vistos])
  const [msgLib, setMsgLib] = useState<string | null>(null)
  const [busyLib, setBusyLib] = useState<string | null>(null)
  const carregarLiberacoes = useCallback(() => {
    void fetchLiberacoes().then((d) => setLiberacoes(d?.liberacoes ?? []))
  }, [])
  useEffect(() => {
    carregarLiberacoes()
  }, [carregarLiberacoes])
  /** Liberar / desfazer: grava no serviço (quem e quando ficam no histórico), que já roda o motor;
   *  depois recarrega a fila para o anúncio sumir (ou voltar) daqui na hora. */
  const liberar = async (a: Anuncio, desfazer: boolean) => {
    if (a.cmv == null) {
      setMsgLib(`${a.mlb}: sem CMV não há o que liberar`)
      return
    }
    setBusyLib(a.mlb)
    setMsgLib(null)
    try {
      if (desfazer) await removerLiberacao(a.mlb)
      else await salvarLiberacao({ mlb: a.mlb, cmv: a.cmv, titulo: a.titulo, conta: contaId, motivo: 'CMV conferido na tela (margem acima de 25%)' })
      carregarLiberacoes()
      ctx.setFila(await fetchFila())
      setMsgLib(desfazer ? `${a.mlb} voltou para a revisão` : `${a.mlb} liberado com CMV ${brl(a.cmv)} — o motor já rodou de novo`)
    } catch (e) {
      setMsgLib(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyLib(null)
    }
  }
  const desfazerLiberacao = async (mlb: string) => {
    setBusyLib(mlb)
    setMsgLib(null)
    try {
      await removerLiberacao(mlb)
      carregarLiberacoes()
      ctx.setFila(await fetchFila())
      setMsgLib(`${mlb} voltou para a revisão`)
    } catch (e) {
      setMsgLib(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyLib(null)
    }
  }
  const liberadoPor = useMemo(() => new Map(liberacoes.map((l) => [l.mlb, l] as const)), [liberacoes])
  const grupos = useMemo<Grupo[]>(() => {
    const porMlb = new Map(conta.ads.map((a) => [a.mlb, a] as const))
    // toda linha `alertar` entra (a antiga seção da aba Robô mostrava todas, com ou sem marca de revisão)
    const decs = (fila?.contas?.[contaId]?.decisoes ?? []).filter((d) => d.revisao_humana || d.acao === 'alertar')
    const g = new Map<string, Grupo>()
    for (const d of decs) {
      const a = porMlb.get(d.mlb)
      if (!a) continue
      const x = g.get(d.mlb) ?? { a, decs: [], motivos: [], sai: 0 }
      x.decs.push(d)
      const motivo = d.revisao_humana || (d.acao === 'alertar' ? d.motivo : null)
      if (motivo && !x.motivos.includes(motivo)) x.motivos.push(motivo)
      if (d.acao === 'sair_auto') x.sai += 1
      g.set(d.mlb, x)
    }
    // quem o robô vai tirar primeiro, depois maior desconto ativo
    return [...g.values()].sort((x, y) => (y.sai - x.sai) || ((y.a.n_promo_ativa ?? 0) - (x.a.n_promo_ativa ?? 0)) || y.a.preco_atual - x.a.preco_atual)
  }, [fila, contaId, conta])
  // visto vale enquanto o tipo do motivo for o mesmo
  const estaVisto = (g: Grupo) => vistoPor.get(g.a.mlb)?.chave === chaveVisto(g.motivos)
  const gruposVisiveis = mostrarVistos ? grupos : grupos.filter((g) => !estaVisto(g))
  const nVistos = grupos.length - grupos.filter((g) => !estaVisto(g)).length
  const nSai = grupos.reduce((n, g) => n + g.sai, 0)
  const nAtivos = grupos.filter((g) => (g.a.n_promo_ativa ?? 0) > 0).length

  // ---- SAIR em massa (veio da aba Robô em 19/10/2026): seleciona anúncios, confirma em dois cliques ----
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [armado, armar, desarmar] = useArmado(6000)
  const [prog, setProg] = useState<string | null>(null)
  const selecionaveis = gruposVisiveis.filter((g) => saiveis(g).length > 0)
  const alvosSel = selecionaveis.filter((g) => sel.has(g.a.mlb)).flatMap((g) => saiveis(g).map(({ p, d }) => ({ a: g.a, p, d })))
  const toggleTodos = () => {
    const todos = selecionaveis.every((g) => sel.has(g.a.mlb))
    setSel(todos ? new Set() : new Set(selecionaveis.map((g) => g.a.mlb)))
  }
  const sairDasSelecionadas = async () => {
    if (!alvosSel.length) { setProg('nenhum anúncio selecionado'); return }
    if (!ctx.escreve) {
      // preview: uma atualização de estado para o lote inteiro (regra de 23/09/2026)
      ctx.marcarLote(alvosSel.map(({ a, p }) => ({ k: selKey(a, p), status: null, preview: true })))
      setProg(`${alvosSel.length} marcada(s) em preview (nada enviado ao ML)`)
      return
    }
    if (!armado) { armar(); return }
    desarmar()
    let ok = 0; let jaFora = 0; let relampago = 0; const erros: string[] = []
    for (let i = 0; i < alvosSel.length; i++) {
      const { a, p, d } = alvosSel[i]
      setProg(`${i + 1}/${alvosSel.length} — ${a.mlb}…`)
      let feito = 'falhou'
      try {
        const r = await ctx.remover(a, p)
        if (r.ok && r.ja_fora) jaFora++
        else if (r.ok) ok++
        else if (r.relampago) relampago++
        else erros.push(`${a.mlb}: HTTP ${r.http ?? 0} ${(r.erro ?? JSON.stringify(r.resposta ?? '')).slice(0, 120)}`)
        feito = r.ok ? `executado HTTP ${r.http}` : `falhou HTTP ${r.http ?? 0}`
      } catch (e) { erros.push(`${a.mlb}: ${e instanceof Error ? e.message : String(e)}`) }
      // mesma trilha da seção antiga da aba Robô: decisão humana registrada em decisoes_humanas.jsonl
      await registrarDecisao({ conta: contaId, mlb: a.mlb, acao: d.acao, decisao: 'aprovado', motivo: feito, promotion_type: d.promocao_tipo, promotion_id: d.promocao_id, margem: d.margem, fila_gerada_em: fila?.gerado_em ?? null })
      await new Promise((res) => setTimeout(res, 150))
    }
    setProg(`${ok} ok${jaFora ? ` · ${jaFora} já estavam fora (nada enviado)` : ''}${relampago ? ` · ${relampago} relâmpago(s) não se removem (regra do ML)` : ''}${erros.length ? ` · ${erros.length} recusados: ${erros.join(' | ')}` : ''} — tudo em escritas_promo.log`)
    setSel(new Set())
    ctx.setFila(await fetchFila())
  }

  return (
    <Panel tone="critico" title={`🧑‍⚖️ Revisão humana — ${grupos.length} ${grupos.length === 1 ? 'anúncio' : 'anúncios'}${nAtivos ? ` · ${nAtivos} em promoção` : ''}${nSai ? ` · ${nSai} ${nSai === 1 ? 'promoção que o robô tira' : 'promoções que o robô tira'}` : ''}`}>
      <P>Anúncios em que o robô <b>não entra em promoção nem relâmpago</b> (cupom entra) até você olhar: qualquer dado faltando (CMV, frete, comissão ou imposto), CMV absurdo, variações sem custo, desconto acima de 30% quando o preço é do robô, promoção relida depois da coleta completa (aguardando a madrugada), ou promoção ativa entre {fila?.regras.margem_saida_automatica ?? 14}% e {fila?.regras.margem_alerta ?? 18}%. Ativa sem margem confiável com desconto acima de 10% o robô tira; até 10% fica. A setinha abre <b>todas as campanhas</b> do anúncio com o botão de cada uma — <b>SAIR</b> na ativa, <b>Participar</b> na candidata (abre o seletor de preço). Se a margem estiver certa, corrija o CMV na aba "Por anúncio" ou clique em <b>exceção</b> (abre o painel de exceções, na aba Robô, já com o MLB). Para tirar várias de uma vez, marque os anúncios e use <b>Sair das selecionadas</b> (cupom fica fora; relâmpago o ML não deixa remover).</P>
      {selecionaveis.length ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Button size="small" variant="outlined" color="inherit" onClick={toggleTodos}>{selecionaveis.every((g) => sel.has(g.a.mlb)) ? 'desmarcar todos' : 'marcar todos'}</Button>
          <Button size="small" variant="contained" color="error" onClick={() => void sairDasSelecionadas()} sx={{ fontWeight: armado ? 700 : 500 }}
            title={ctx.escreve ? 'Tira os anúncios marcados das promoções ativas apontadas pelo motor (alerta/sair). Confirmação em dois cliques.' : 'Preview: sem o serviço no ar, nada é enviado ao Mercado Livre.'}>
            {armado ? `⚠ Confirmar: ${alvosSel.length} chamada(s) reais à API` : '🔻 Sair das selecionadas'}
          </Button>
          {prog ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{prog}</Typography> : null}
        </Stack>
      ) : null}
      {msgLib ? <Typography sx={{ fontSize: 12, mb: 0.5 }}>{msgLib}</Typography> : null}
      {liberacoes.length ? (
        <Box sx={{ mb: 0.75 }}>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
            ✅ {liberacoes.length} {liberacoes.length === 1 ? 'anúncio liberado' : 'anúncios liberados'} por você (CMV conferido) — somem daqui enquanto o custo não mudar. Para desfazer:
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {liberacoes.map((l) => (
              <Button key={l.mlb} size="small" variant="text" color="inherit" disabled={!ctx.escreve || busyLib === l.mlb} sx={{ fontSize: 11, textTransform: 'none' }}
                title={`${l.titulo ?? ''} · CMV conferido ${brl(l.cmv)} · por ${l.por ?? 'tela'}`}
                onClick={() => void desfazerLiberacao(l.mlb)}>
                ✕ {l.mlb}
              </Button>
            ))}
          </Stack>
        </Box>
      ) : null}
      {nVistos ? (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 0.5 }}>
          👁 {nVistos} {nVistos === 1 ? 'anúncio marcado como visto' : 'anúncios marcados como visto'} (escondidos até o motivo mudar).{' '}
          <Button size="small" variant="text" color="inherit" sx={{ fontSize: 11, textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => setMostrarVistos((v) => !v)}>
            {mostrarVistos ? 'esconder' : 'mostrar'}
          </Button>
        </Typography>
      ) : null}
      {gruposVisiveis.length ? gruposVisiveis.map((g) => (
        <LinhaAnuncio
          key={g.a.mlb}
          g={g}
          selecionavel={saiveis(g).length > 0}
          sel={sel.has(g.a.mlb)}
          onSel={(on) => setSel((x) => { const n = new Set(x); if (on) n.add(g.a.mlb); else n.delete(g.a.mlb); return n })}
          liberavel={g.motivos.some((m) => MOTIVO_MARGEM_ALTA.test(m)) && ctx.escreve}
          liberado={liberadoPor.get(g.a.mlb)}
          visto={estaVisto(g)}
          busy={busyLib === g.a.mlb}
          onLiberar={(desfazer) => void (desfazer ? desfazerLiberacao(g.a.mlb) : liberar(g.a, false))}
          onVisto={(desfazer) => void marcarVisto(g.a, g.motivos, desfazer)}
        />
      )) : <Typography sx={{ color: 'text.disabled', fontSize: 13 }}>{grupos.length ? 'Tudo visto. Nada novo na revisão.' : 'Nenhum anúncio em revisão na fila de hoje.'}</Typography>}
    </Panel>
  )
}

function LinhaAnuncio({ g, selecionavel, sel, onSel, liberavel, liberado, visto, busy, onLiberar, onVisto }: { g: Grupo; selecionavel: boolean; sel: boolean; onSel: (on: boolean) => void; liberavel: boolean; liberado?: Liberacao; visto: boolean; busy: boolean; onLiberar: (desfazer: boolean) => void; onVisto: (desfazer: boolean) => void }) {
  const ctx = usePromo()
  const [aberto, setAberto] = useState(false)
  const { a } = g
  const ativas = a.promocoes.filter((p) => p.status === 'started' || p.status === 'pending')
  const candidatas = a.promocoes.filter((p) => p.status === 'candidate')
  const principal = g.decs.find((d) => d.acao === 'sair_auto') ?? g.decs.find((d) => d.acao !== 'cupom' && d.acao !== 'relampago') ?? g.decs[0]
  // exceção com prazo só faz sentido onde o robô tiraria ou alerta (mesma condição da antiga seção da aba Robô)
  const temExcecao = g.decs.some((d) => d.acao === 'sair_auto' || d.acao === 'alertar')
  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', py: 1 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '24px 1fr', md: '24px 1fr auto auto' }, gap: 1.75, alignItems: 'center', fontSize: 13 }}>
        <Box>{selecionavel ? <Checkbox size="small" checked={sel} onChange={(e) => onSel(e.target.checked)} sx={{ p: 0 }} title="Marcar para o Sair das selecionadas" /> : null}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={a.titulo}>{a.titulo}</Typography>
          <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
            <Box component="span" sx={mono}>{a.mlb}</Box>{a.sku ? ` · ${a.sku}` : ''} · preço cheio <b>{brl(a.preco_atual)}</b>{a.cmv != null ? ` · CMV ${brl(a.cmv)}` : ' · sem CMV'}{a.mc_cheio_pct != null ? ` · MC no cheio ${pct(a.mc_cheio_pct)}` : ''} · {ativas.length} ativa{ativas.length === 1 ? '' : 's'} · {candidatas.length} candidata{candidatas.length === 1 ? '' : 's'}
          </Typography>
          {g.motivos.map((m) => <Typography key={m} sx={{ fontSize: 11, color: LARANJA }}>{m}</Typography>)}
        </Box>
        <Box sx={{ fontSize: 12, color: g.sai ? 'error.main' : 'text.secondary', whiteSpace: 'nowrap' }}>
          {principal ? <>{ROTULO_ROBO[principal.acao] ?? principal.acao}{principal.promocao_nome ? ` · ${principal.promocao_nome}` : ''}{principal.margem != null ? ` · MC ${pct(principal.margem)}` : ''}</> : null}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {liberavel ? (
            <Button
              size="small"
              variant={liberado ? 'text' : 'contained'}
              color={liberado ? 'inherit' : 'success'}
              disabled={busy || a.cmv == null}
              onClick={() => onLiberar(Boolean(liberado))}
              title={liberado
                ? `Liberado por ${liberado.por ?? 'tela'} com CMV ${brl(liberado.cmv)}; o CMV mudou para ${brl(a.cmv ?? 0)}, por isso voltou. Clique para desfazer a liberação antiga.`
                : `Você conferiu o CMV de ${brl(a.cmv ?? 0)} e está certo: o anúncio sai desta lista e o robô passa a tratá-lo como qualquer outro, respeitando os pisos. A liberação cai sozinha se o custo mudar.`}
            >
              {busy ? '…' : liberado ? 'desfazer liberação' : '✓ Liberar (CMV conferido)'}
            </Button>
          ) : null}
          {ativas.slice(0, 1).map((p) => <AcaoCell key={p.id} a={a} p={p} />)}
          {temExcecao ? <Button size="small" variant="text" color="inherit" title="Marcar exceção com prazo: o robô não tira este anúncio até a data (abre o painel de exceções na aba Robô)" onClick={() => { ctx.abrirRobo(); pedirExcecao(a.mlb, a.titulo) }}>exceção</Button> : null}
          <Button size="small" variant={visto ? 'text' : 'outlined'} color="inherit" disabled={busy || !ctx.escreve} onClick={() => onVisto(visto)}
            title={visto ? 'Marcado como visto: clique para voltar a mostrar' : 'OK, visto: some daqui até o motivo mudar. Não muda nada no robô.'}>
            {visto ? '👁 visto' : 'OK, visto'}
          </Button>
          <Button size="small" variant="outlined" color="inherit" onClick={() => ctx.abrirAnuncio(a.mlb)}>Conferir</Button>
          <Button size="small" variant={aberto ? 'contained' : 'outlined'} onClick={() => setAberto((v) => !v)} title="Abre todas as campanhas disponíveis para este anúncio, com o botão de entrar ou sair em cada uma">
            {aberto ? '▲' : '▼'} campanhas
          </Button>
        </Stack>
      </Box>
      <Collapse in={aberto} unmountOnExit>
        <Box sx={{ mt: 1, ml: { md: 2 }, borderLeft: 2, borderColor: 'divider', pl: 1.5 }}>
          {a.promocoes.length ? a.promocoes
            .slice()
            .sort((x, y) => ordem(x) - ordem(y))
            .map((p) => <LinhaPromo key={selKey(a, p)} a={a} p={p} />)
            : <Typography sx={{ color: 'text.disabled', fontSize: 12 }}>Nenhuma promoção disponível na API para este anúncio.</Typography>}
        </Box>
      </Collapse>
    </Box>
  )
}

const ordem = (p: Promocao) => (p.status === 'started' ? 0 : p.status === 'pending' ? 1 : p.status === 'candidate' ? 2 : 3)

function LinhaPromo({ a, p }: { a: Anuncio; p: Promocao }) {
  const ctx = usePromo()
  const cor = useCorMc()
  const m = mcDe(a, p, ctx.base, ctx.cmvManual)
  const desc = p.preco_promo != null && a.preco_atual ? (1 - p.preco_promo / a.preco_atual) * 100 : null
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'auto 1fr auto auto' }, gap: 1.5, alignItems: 'center', py: 0.6, fontSize: 12.5 }}>
      <StatusPill p={p} k={selKey(a, p)} />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontSize: 12.5 }}>{p.name || p.type_label}<Box component="span" sx={{ color: 'text.disabled' }}> · {p.type_label}</Box></Typography>
        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>
          {p.preco_promo != null ? <>de {brl(p.original_price ?? a.preco_atual)} por <b>{brl(p.preco_promo)}</b>{desc != null ? ` (−${desc.toFixed(0)}%)` : ''}</> : 'sem preço (aceite direto)'}
          {p.min_discounted_price != null && p.max_discounted_price != null ? ` · faixa ${brl(p.min_discounted_price)}–${brl(p.max_discounted_price)}` : ''}
          {p.ml_pct ? ` · ML banca ${p.ml_pct}%` : ''}
          {p.finish_date ? ` · até ${p.finish_date.slice(0, 10).split('-').reverse().join('/')}` : ''}
        </Typography>
      </Box>
      <McTip a={a} p={p}>
        <Box component="b" sx={{ color: cor(classeMc(m, ctx.alvo)), whiteSpace: 'nowrap' }}>{m.rs == null ? '—' : `${brl(m.rs)} · ${pct(m.pct)}`}</Box><IncMark faltas={m.faltas} />
      </McTip>
      <AcaoCell a={a} p={p} icone />
    </Box>
  )
}


/** Ela, 01/10/2026: "importante me sinalizar esses casos que poderiam passar do teto de 20%, pode ser que
 *  manualmente eu mexa e aumente o desconto". Entradas em que o robô parou no teto: a margem permitiria mais. */
export function TetoDesconto() {
  const { fila, contaId, conta } = usePromo()
  const itens = useMemo(() => {
    const porMlb = new Map(conta.ads.map((a) => [a.mlb, a] as const))
    return (fila?.contas?.[contaId]?.decisoes ?? [])
      .filter((d) => d.teto_20 && (d.acao === 'entrar' || d.acao === 'trocar'))
      .map((d) => ({ d, a: porMlb.get(d.mlb) }))
      .sort((x, y) => (y.a?.faturamento_30d ?? 0) - (x.a?.faturamento_30d ?? 0))
  }, [fila, contaId, conta])
  if (!itens.length) return null
  return (
    <Panel title={`🧢 Teto de 20% de desconto — ${itens.length} ${itens.length === 1 ? 'entrada parou no teto' : 'entradas pararam no teto'}`}>
      <P>Nestes anúncios a margem permitiria desconto maior, mas o robô parou em 20% do preço cheio (sua regra de 01/10). Se quiser mais desconto, aumente na mão na campanha; o robô não desfaz.</P>
      {itens.map(({ d, a }) => (
        <Box key={`${d.mlb}|${d.promocao_id}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 1, borderTop: 1, borderColor: 'divider', py: 0.6, fontSize: 12.5 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" noWrap title={d.titulo}>{d.titulo}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}><Box component="span" sx={mono}>{d.mlb}</Box> · {d.promocao_tipo}{d.promocao_nome ? ` · ${d.promocao_nome}` : ''} · preço cheio {brl(d.preco_atual)}</Typography>
          </Box>
          <Typography sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            robô: <b>{brl(d.preco_promo ?? 0)}</b> (20% off, MC {pct(d.margem ?? 0)}) · a meta permitiria até <b>{d.preco_sem_teto != null ? brl(d.preco_sem_teto) : '—'}</b>{a?.faturamento_30d ? ` · ${brl(a.faturamento_30d)}/30d` : ''}
          </Typography>
        </Box>
      ))}
    </Panel>
  )
}
